import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Award, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { HERO_SLIDES } from "@/lib/images";
import { useInquiry } from "@/context/InquiryContext";

const AUTOPLAY = 6500;
const LINES = ["Laboratory Equipment", "Built to Exact", "Standards."];

export default function HeroSlider() {
  const [index, setIndex] = useState(0);
  const { openQuote } = useInquiry();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  useEffect(() => {
    const t = setTimeout(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), AUTOPLAY);
    return () => clearTimeout(t);
  }, [index]);

  const slide = HERO_SLIDES[index];
  const prev = () => setIndex((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  const next = () => setIndex((i) => (i + 1) % HERO_SLIDES.length);

  return (
    <section
      ref={heroRef}
      data-testid="hero-slider"
      className="relative h-[calc(100vh-104px)] min-h-[560px] overflow-hidden bg-navy lg:h-[calc(100vh-112px)]"
    >
      <AnimatePresence>
        <motion.div
          key={index}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          style={{ y: imgY }}
        >
          <motion.img
            src={slide.url}
            alt={slide.caption}
            className="h-full w-full object-cover"
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: AUTOPLAY / 1000 + 0.9, ease: "linear" }}
          />
        </motion.div>
      </AnimatePresence>

      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/55 to-slate-900/15" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-navy/80 to-transparent" />

      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-center px-6">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-rose-300"
        >
          GSTIN 33AINPR0146B1ZM · Chennai · Est. 2006
        </motion.p>

        <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
              >
                {i === 2 ? <span className="text-rose-400">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-6 h-12 max-w-xl sm:text-lg">
          <AnimatePresence mode="wait">
            <motion.p
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
              className="text-sm leading-relaxed text-white/70 sm:text-base"
            >
              {slide.caption}
            </motion.p>
          </AnimatePresence>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-8 flex flex-wrap items-center gap-3"
        >
          <Link
            to="/products"
            data-testid="hero-cta-products"
            className="inline-flex items-center gap-2 rounded-lg bg-safety px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-sky-900/30 transition hover:bg-safety-dark"
          >
            Explore Products <ArrowRight size={16} />
          </Link>
          <button
            data-testid="hero-cta-quote"
            onClick={() => openQuote()}
            className="inline-flex items-center gap-2 rounded-lg border border-white/40 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-white hover:text-navy"
          >
            Get a Quote
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-8 flex flex-wrap gap-2"
        >
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80 backdrop-blur">
            <ShieldCheck size={12} className="text-rose-400" /> GSTIN 33AINPR0146B1ZM
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80 backdrop-blur">
            <Award size={12} className="text-rose-400" /> Est. 2006 · Chennai
          </span>
        </motion.div>
      </div>

      {/* Controls */}
      <div className="absolute inset-x-0 bottom-0 z-10 border-t border-white/15 bg-navy/30 backdrop-blur-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-4">
          <div className="flex items-center gap-3">
            <button data-testid="hero-prev-btn" onClick={prev} aria-label="Previous slide" className="rounded-full border border-white/30 p-2 text-white transition hover:bg-white hover:text-navy">
              <ArrowLeft size={15} />
            </button>
            <button data-testid="hero-next-btn" onClick={next} aria-label="Next slide" className="rounded-full border border-white/30 p-2 text-white transition hover:bg-white hover:text-navy">
              <ArrowRight size={15} />
            </button>
            <span className="ml-2 font-mono text-xs tracking-widest text-white/80">
              {String(index + 1).padStart(2, "0")} / {String(HERO_SLIDES.length).padStart(2, "0")}
            </span>
          </div>
          <div className="h-[2px] w-24 overflow-hidden rounded bg-white/20 sm:w-40">
            <motion.div
              key={index}
              className="h-full bg-rose-400"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: AUTOPLAY / 1000, ease: "linear" }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
